import { useApi } from './apiService';

interface GetSoftwareAssetsParams {
  tenantId: string;
  isSupported?: boolean;
  pageNumber?: number;
  pageSize?: number;
  severity?: string;
  kevExploited?: boolean;
  epssThreshold?: number;
  search?: string;
}

interface SubscribedProduct {
  name: string;
  category: string;
  dateSubScribed: string;
}

// New SentinelOne endpoint pattern
const getSentinelOneVulnerabilitiesEndpoint = (tenantId: string, pageNumber = 1, pageSize = 20) =>
  `vulnerabilityservice/Sentinelone/${tenantId}/software-vulnerabilities?pageNumber=${pageNumber}&pageSize=${pageSize}`;

const getSentinelOneBannerEndpoint = (tenantId: string) => `vulnerabilityservice/Sentinelone/${tenantId}/software-vulnerabilities/banner`;

const getManagedPackageByIdentifierEndpoint = (packageIdentifier: string) =>
  `/patchreposervice/InstallerManifest/defatom-managed-package/${encodeURIComponent(packageIdentifier)}`;

export const useVulnerabilityService = () => {
  const api = useApi();

  const getSubscribedVulnerabilityProducts = async (tenantId: string) => {
    const response = await api.get(`marketplaceservice/MarketPlace/subscribed-products/${tenantId}?category=vulnerability`);
    return response.data as {
      data?: SubscribedProduct[];
      message?: string;
      isSuccess?: boolean;
      errorCode?: string | null;
    };
  };

  const getVulnerabilityBanner = async (tenantId: string) => {
    const response = await api.get(`patchreposervice/SoftwareAssetInventory/${tenantId}/vulnerability-banner`);
    return response.data;
  };

  const getSoftwareAssets = async (params: GetSoftwareAssetsParams) => {
    const queryParams = new URLSearchParams();

    if (params.isSupported !== undefined) queryParams.append('isSupported', String(params.isSupported));
    if (params.pageNumber) queryParams.append('pageNumber', String(params.pageNumber));
    if (params.pageSize) queryParams.append('pageSize', String(params.pageSize));
    if (params.severity) queryParams.append('severity', params.severity);
    if (params.kevExploited !== undefined) queryParams.append('kevExploited', String(params.kevExploited));
    if (params.epssThreshold !== undefined) queryParams.append('epssThreshold', String(params.epssThreshold));
    if (params.search) queryParams.append('search', params.search);

    const response = await api.get(`patchreposervice/SoftwareAssetInventory/${params.tenantId}/software-assets?${queryParams.toString()}`);
    return response.data;
  };

  const getSoftwareAssetSummary = async (tenantId: string, softwareAssetInventoryId: string) => {
    const response = await api.get(`assetmgtservice/SoftwareAssetInventory/${tenantId}/software-asset-summary/${softwareAssetInventoryId}`);
    return response.data;
  };

  const getSoftwareAssetDevices = async (tenantId: string, softwareAssetInventoryId: string) => {
    const response = await api.get(
      `assetmgtservice/SoftwareAssetInventory/${tenantId}/software-asset-by-agent/${softwareAssetInventoryId}?pageNumber=1&pageSize=1000`,
    );
    return response.data;
  };

  const getSentinelOneSoftwareVulnerabilities = async (tenantId: string, pageNumber = 1, pageSize = 20) => {
    const response = await api.get(getSentinelOneVulnerabilitiesEndpoint(tenantId, pageNumber, pageSize));
    return response.data;
  };

  const getSentinelOneBanner = async (tenantId: string) => {
    const response = await api.get(getSentinelOneBannerEndpoint(tenantId));
    return response.data;
  };

  const getManagedPackageByIdentifier = async (packageIdentifier: string) => {
    const response = await api.get(getManagedPackageByIdentifierEndpoint(packageIdentifier));
    return response.data;
  };

  return {
    getVulnerabilityBanner,
    getSoftwareAssets,
    getSoftwareAssetSummary,
    getSoftwareAssetDevices,
    getSentinelOneSoftwareVulnerabilities,
    getSentinelOneBanner,
    getManagedPackageByIdentifier,
    getSubscribedVulnerabilityProducts,
  };
};
